import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Progress } from 'antd';
import DocumentsContainer from 'common/documents/containers/documents-container';
import SubMenu from 'common/navigation/components/sub-menu';
import MainMenu from 'common/navigation/containers/main-menu-container';
import { changeNavigationContext } from 'common/navigation/containers/navigation-actions';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import styled from 'styled-components';
import { Flex, notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import { ticketObjectRoutes } from '../../../common/navigation/routes/cops-routes';
import TicketWorklistContainer from '../../../tickets/containers/list/ticket-worklist-container';
import DetailsContainer from './details-container';

const NumberOfOrders = styled.span`
  font-size: 30px;
`;
const ProgressTitle = styled.span`
  text-align: center;
  font-weight: ${p => p.theme.fontWeight.bold};
`;

const CustomProgress = styled(Progress)`
  .ant-progress-bg {
    background-color: #108ee9 !important;
  }
`;

const StyledButton = styled(Button)`
  background-color: transparent;
  border-color: rgba(0, 0, 0, 0.65);
  &:disabled {
    background-color: transparent;
    &:hover {
      background-color: transparent;
    }
  }
  &:hover {
    background-color: transparent;
  }
`;

class MultipleDetailsContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      blockBackNavigation: false,
      orderIds: props.orderIds,
      position:
        props.currentPosition &&
          (props.currentPosition >= 0 && props.currentPosition < props.orderIds.length)
          ? props.currentPosition
          : 0,
    };
  }

  componentDidMount() {
    this.props.changeNavigationContext({
      context: this.props.orderIds[0],
      shouldUpdate: true,
    });
  }

  /**
   * if in the context of multiple orders an order is deleted,
   * refresh without the deleted order in the url.
   */
  componentWillReceiveProps(nextProps) {
    // TODO: check if this is not an anti-pattern
    if (
      (
        (nextProps.removeSuccess && !this.props.removeSuccess) ||
        (nextProps.isExternalRemove &&
          !this.props.isExternalRemove &&
          !nextProps.removeSuccess &&
          !this.props.removeSuccess
        )
      ) &&
      nextProps.orderIds.length > 1
    ) {
      const { currentPosition, orderIds, position } = this.state;
      const newIds = orderIds?.filter((i, e) => e !== position);
      setTimeout(() => {
        this.setState({
          position:
            currentPosition && (currentPosition >= 0 && currentPosition < orderIds.length)
              ? currentPosition
              : 0,
          orderIds: newIds,
        });
        nextProps.history.push(`/order/${newIds?.map(o => encodeURIComponent(o)).join(',')}`, {
          context: newIds[0],
        });
      }, nextProps.isExternalRemove && !nextProps.removeSuccess && !this.props.removeSuccess ?
        5000 :
        0,
      );
    } else if (
      nextProps.orderIds.length < this.props.orderIds.length
    ) {
      this.setState({
        blockBackNavigation: true,
      });
    }
    if (nextProps.history.location.pathname !== this.props.history.location.pathname) {
      this.setState({
        orderIds: nextProps.orderIds,
        position:
          nextProps.currentPosition &&
            // eslint-disable-next-line
            (nextProps.currentPosition >= 0 && nextProps.currentPosition < nextProps.orderIds.length)
            ? nextProps.currentPosition
            : 0,
      });
    }
  }

  /**
   * Get the render function of the menu, used within the pages.
   * Passes the currently active order id as id to MainMenu
   */
  getMenuRenderer = () => {
    const { orderIds, position } = this.state;
    return actionButtons =>
      <MainMenu
        id={orderIds[position]}
        type='order'
        actionButtons={actionButtons}
        forceActive={true}
      >
        <SubMenu
          id={orderIds[position]}
          hasTickets={true}
          hasInfo={false}
          baseType='order'
          base={`order/${orderIds?.map(o => encodeURIComponent(o)).join(',')}`}
          dashboardTranslation='nav.subNav.DETAILS'
          extraLinks={
            [
              {
                translationKey: 'nav.subNav.DOCUMENTS',
                uri: `/order/${orderIds?.map(o => encodeURIComponent(o)).join(',')}/documents`,
                absolute: true,
              },
            ]
          }
        />
      </MainMenu>;
  };

  /**
   * Activate the next order of the array.
   */
  nextOrder = (hasChanges) => {
    const { position, orderIds } = this.state;
    const newPosition = position + 1;
    if (hasChanges) {
      this.showNotification();
    }
    if (!hasChanges && newPosition < orderIds.length) {
      this.navigateAway(newPosition);
    }
  };

  /**
   * Activate the previous order of the array.
   */
  previousOrder = (hasChanges) => {
    const { position } = this.state;
    const newPosition = position - 1;
    if (hasChanges) {
      this.showNotification();
    }
    if (!hasChanges && newPosition >= 0) {
      this.navigateAway(newPosition);
    }
  };

  showNotification = () => {
    const { intl } = this.props;
    notification.warning({
      message: intl.formatMessage({ id: 'statusBar.PENDING_CHANGES_TITLE' }),
      description: intl.formatMessage({ id: 'statusBar.PENDING_CHANGES_MESSAGE' }),
      key: 'pendingChanges',
      className: 'extended-notification',
      duration: 0,
      placement: 'top',
    });
  };

  navigateAway = (newPosition) => {
    const { orderIds } = this.state;
    const { history } = this.props;
    this.setState({ position: newPosition });
    history.replace(
      `/order/${orderIds?.map(o => encodeURIComponent(o)).join(',')}/position/${newPosition}`,
    );
    this.props.changeNavigationContext({
      context: orderIds[newPosition],
      shouldUpdate: true,
    });
  };

  render() {
    const { orderIds, position, blockBackNavigation } = this.state;
    const { intl } = this.props;
    const ticketRoutes = ticketObjectRoutes();
    const renderMenu = this.getMenuRenderer();
    return (
      <>
        <Routes>
          <Route
            path={'dashboard/*'}
            element={<DetailsContainer
              renderMenu={renderMenu}
              id={orderIds[position]}
              isBackNavigationBlocked={blockBackNavigation}
              hasMultipleOrders={orderIds.length > 1}
              progressInfo={({ isSaving, hasChanges, isCollapsedStatusBar }) =>
                <Flex fdc>
                  {!isCollapsedStatusBar ?
                    <ProgressTitle>
                      <FormattedMessage id='order.workingOn' />
                    </ProgressTitle> :
                    ''
                  }
                  <Flex jcsb aic ffrw>
                    <StyledButton
                      icon={<LeftOutlined />}
                      onClick={() => this.previousOrder(hasChanges)}
                      disabled={position === 0 || isSaving}
                    />
                    <NumberOfOrders>
                      {intl.formatMessage(
                        { id: 'statusBar.MULTIPLEBAR_TITLE' },
                        { currentPosition: position + 1, orders: orderIds.length },
                      )}
                    </NumberOfOrders>
                    <StyledButton
                      icon={<RightOutlined />}
                      onClick={() => this.nextOrder(hasChanges)}
                      disabled={position + 1 === orderIds.length || isSaving}
                    />
                  </Flex>
                  {!isCollapsedStatusBar ?
                    <CustomProgress
                      percent={((position + 1) / orderIds.length) * 100}
                      showInfo={false}
                    /> :
                    ''
                  }
                </Flex>
              }
            />}
          />
          <Route
            path={'documents'}
            element={
              <DocumentsContainer renderMenu={renderMenu} id={orderIds[position]} />
            }
          />
          {ticketRoutes?.map((r, i) => <Route
            path={`${r}/*`}
            key={i}
            element={<TicketWorklistContainer
              renderMenu={renderMenu}
              baseType='order'
            />}
          />)}
          <Route
            path='/*'
            element={<Navigate to='dashboard' replace />}
          />
        </Routes>
        <Outlet />
      </>
    );
  }
}

MultipleDetailsContainer.propTypes = {
  history: PropTypes.object.isRequired,
  changeNavigationContext: PropTypes.func.isRequired,
  orderIds: PropTypes.array,
  removeSuccess: PropTypes.bool,
  currentPosition: PropTypes.number,
  intl: PropTypes.object,
  isExternalRemove: PropTypes.bool,
};

const mapDispatchToProps = {
  changeNavigationContext,
};

const mapStateToProps = state => ({
  removeSuccess: state.order.details.removeSuccess,
  isExternalRemove: state.order.details.isExternalRemove,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(withRouter(MultipleDetailsContainer)));
